'use client';

import { Loader2, CheckCircle, AlertCircle, Film, Download, Eye, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Tool UI for Compound Video tool
 * Shows progress for creating highlight reels from multiple moments
 * Includes polling for long-running operations
 */
export function CompoundVideoUI({ part, status }: any) {
  const result = part.result;
  const [pollStatus, setPollStatus] = useState<'polling' | 'ready' | 'failed'>('polling');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Poll for compound video completion
  useEffect(() => {
    if (status === 'done' && result?.success && result?.jobId && !result?.downloadUrl) {
      const jobId = result.jobId;
      let pollCount = 0;
      const maxPolls = 150; // 5 minutes max (2s intervals)

      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const response = await fetch(`/api/video/compound-status?jobId=${jobId}`);
          const data = await response.json();

          if (data.progress) {
            setProgress(data.progress);
          }

          if (data.status === 'completed' && data.videoUrl) {
            setPollStatus('ready');
            setVideoUrl(data.videoUrl);
            clearInterval(pollInterval);
            toast.success('Highlight reel ready!');
          } else if (data.status === 'failed') {
            setPollStatus('failed');
            setError(data.error || 'Video compilation failed');
            clearInterval(pollInterval);
            toast.error('Highlight reel failed');
          } else if (pollCount >= maxPolls) {
            setPollStatus('failed');
            setError('Video compilation timeout (5 minutes exceeded)');
            clearInterval(pollInterval);
            toast.error('Compilation timeout');
          }
        } catch (err) {
          console.error('[CompoundVideoUI] Poll error:', err);
          if (pollCount >= maxPolls) {
            setPollStatus('failed');
            setError('Failed to check compilation status');
            clearInterval(pollInterval);
          }
        }
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(pollInterval);
    }
  }, [status, result]);

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Starting highlight reel creation...
        </span>
      </div>
    );
  }

  if (!result.success) {
    const isCreditsError = result.error?.toLowerCase().includes('credit');
    const upgradeUrl = result.upgradeUrl;

    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 overflow-hidden">
        <div className="flex items-start gap-2 p-3">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 dark:text-red-100">
              {isCreditsError ? 'Insufficient credits' : 'Highlight reel failed'}
            </p>
            {result.error && (
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                {result.error}
              </p>
            )}
          </div>
        </div>
        {isCreditsError && upgradeUrl && (
          <div className="px-3 pb-3">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => window.open(upgradeUrl, '_blank')}
            >
              Upgrade Plan
            </Button>
          </div>
        )}
      </div>
    );
  }

  // If downloadUrl is already in result, show ready state immediately
  if (result.downloadUrl) {
    const momentCount = result.momentCount || result.moments?.length || 0;
    const totalDuration = result.totalDuration || 0;

    return (
      <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
        <div className="flex items-center gap-2 p-3">
          <CheckCircle className="w-4 h-4 text-purple-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
              Highlight reel complete!
            </p>
          </div>
          <Film className="w-4 h-4 text-purple-600" />
        </div>

        <div className="px-3 pb-3 space-y-3">
          <div className="flex items-center gap-4 text-xs text-purple-700 dark:text-purple-300">
            <div className="flex items-center gap-1">
              <Film className="w-3 h-3" />
              <span>{momentCount} moment{momentCount !== 1 ? 's' : ''}</span>
            </div>
            {totalDuration > 0 && (
              <>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>{Math.floor(totalDuration / 60)}:{String(Math.floor(totalDuration % 60)).padStart(2, '0')} duration</span>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => window.open(result.downloadUrl, '_blank')}
            >
              <Download className="w-3 h-3 mr-1" />
              Download
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => {
                // Open video player or preview
                console.log('[CompoundVideoUI] Watch clicked');
              }}
            >
              <Eye className="w-3 h-3 mr-1" />
              Watch
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Polling state
  if (pollStatus === 'polling') {
    const momentCount = result.momentCount || result.moments?.length || 0;
    const estimatedTime = result.estimatedTime || 180; // Default 3 minutes

    return (
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 overflow-hidden">
        <div className="flex items-center gap-2 p-3">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Creating highlight reel...
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Combining {momentCount} moment{momentCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="px-3 pb-3 space-y-2">
          <div className="text-xs text-blue-700 dark:text-blue-300">
            Estimated time: ~{Math.ceil(estimatedTime / 60)} minute{estimatedTime > 60 ? 's' : ''}
          </div>

          {/* Progress bar */}
          <div className="w-full bg-blue-100 dark:bg-blue-900/40 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {progress > 0 && (
            <div className="text-xs text-blue-600 dark:text-blue-400 text-right">
              {progress}%
            </div>
          )}
        </div>
      </div>
    );
  }

  // Ready state (from polling)
  if (pollStatus === 'ready' && videoUrl) {
    const momentCount = result.momentCount || result.moments?.length || 0;

    return (
      <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
        <div className="flex items-center gap-2 p-3">
          <CheckCircle className="w-4 h-4 text-purple-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
              Highlight reel complete!
            </p>
          </div>
        </div>

        <div className="px-3 pb-3 space-y-2">
          <div className="text-xs text-purple-700 dark:text-purple-300">
            {momentCount} moments combined
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => window.open(videoUrl, '_blank')}
            >
              <Download className="w-3 h-3 mr-1" />
              Download
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => {
                console.log('[CompoundVideoUI] Watch clicked');
              }}
            >
              <Eye className="w-3 h-3 mr-1" />
              Watch
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (pollStatus === 'failed') {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Highlight reel failed
          </p>
          {error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
}
